import { buildUrl, usKidsGolfService } from '@/libs/api/instances/us-kids-golf-instance';
import type { RequestOptions } from '@/libs/api/types';

/** Path (relative to `buildUrl`) for scheduling the signed-in guardian's account deletion. */
export const accountDeletionEndpoint = () => '/guardians/me/deletion';

/** Body for POST /api/v1/guardians/me/deletion. */
export type ScheduleAccountDeletionRequest = {
  /** Optional free-text reason (≤200 chars), forwarded to admin in the notification email. */
  reason?: string;
  /** The guardian ticked "I understand this can't be undone after 30 days." (must be true). */
  acknowledged: boolean;
};

/** Response of POST /api/v1/guardians/me/deletion. */
export type ScheduleAccountDeletionResponse = {
  /** New account status — `pending_deletion` once the hold is set. */
  status: string;
  /** ISO timestamp when the account is permanently deleted (end of the 30-day hold). */
  scheduledAt: string;
};

/**
 * Schedules the signed-in guardian's account for permanent deletion after 30 days via
 * POST `/api/v1/guardians/me/deletion`. The server retains all data during the 30-day hold
 * (BR-03), emails admin the reason, and restores the account if the guardian logs back in
 * before the deadline. Logging out + returning to Welcome is handled by the caller.
 */
export async function scheduleAccountDeletion(
  body: ScheduleAccountDeletionRequest,
  options?: RequestOptions,
): Promise<ScheduleAccountDeletionResponse> {
  return await usKidsGolfService.post<
    ScheduleAccountDeletionResponse,
    ScheduleAccountDeletionRequest
  >(buildUrl(accountDeletionEndpoint()), body, options);
}

/**
 * Cancels a pending account deletion via DELETE `/api/v1/guardians/me/deletion`, restoring
 * the account to active (BR-03: within the 30-day hold). Called automatically after login
 * when the guardian's profile comes back with `status: 'pending_deletion'`.
 */
export async function cancelAccountDeletion(options?: RequestOptions): Promise<void> {
  await usKidsGolfService.delete<void>(buildUrl(accountDeletionEndpoint()), options);
}
